import { useState, useEffect, useCallback, useRef } from 'react'
import { loadApi } from '@/services/api'
import { LoadDetails, UseLoadDetailsReturn } from '@/types/load'

interface UseLoadDetailsOptions {
  loadNumber?: string | null
  loadId?: string | null
}

export const useLoadDetails = (
  loadNumberOrOptions: string | null | UseLoadDetailsOptions
): UseLoadDetailsReturn => {
  // Handle both old signature (string) and new signature (options object)
  const isSimpleArg =
    typeof loadNumberOrOptions === 'string' || loadNumberOrOptions === null

  const loadNumber: string | null | undefined = isSimpleArg
    ? (loadNumberOrOptions as string | null)
    : (loadNumberOrOptions as UseLoadDetailsOptions).loadNumber

  const loadId: string | null | undefined = isSimpleArg
    ? undefined
    : (loadNumberOrOptions as UseLoadDetailsOptions).loadId

  const [load, setLoad] = useState<LoadDetails | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const latestRequestRef = useRef(0)

  const fetchLoad = useCallback(async () => {
    // Latest request wins: a slower older response must not overwrite a newer one
    const requestId = ++latestRequestRef.current
    const isLatest = () => requestId === latestRequestRef.current

    // If we have loadId, use it directly (linked via B18)
    if (loadId) {
      try {
        setLoading(true)
        setError(null)

        const loadData = await loadApi.getLoadById(loadId)
        if (!isLatest()) return

        if (loadData) {
          setLoad(loadData as LoadDetails)
        } else {
          setLoad(null)
          setError(new Error(`Load not found`))
        }
      } catch (err) {
        if (!isLatest()) return
        setError(err as Error)
        setLoad(null)
      } finally {
        if (isLatest()) setLoading(false)
      }
      return
    }

    // Otherwise use load number to search
    if (!loadNumber) {
      setLoad(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const loadData = await loadApi.getLoadByNumber(loadNumber)
      if (!isLatest()) return

      if (loadData) {
        setLoad(loadData as LoadDetails)
      } else {
        setLoad(null)
        setError(new Error(`Load #${loadNumber} not found`))
      }
    } catch (err) {
      if (!isLatest()) return
      setError(err as Error)
      setLoad(null)
    } finally {
      if (isLatest()) setLoading(false)
    }
  }, [loadNumber, loadId])

  useEffect(() => {
    fetchLoad()
  }, [fetchLoad])

  const refetch = useCallback(() => {
    fetchLoad()
  }, [fetchLoad])

  return {
    load,
    loading,
    error,
    refetch
  }
}

export default useLoadDetails
